/**
 * "Your styles" (ADR-037 D2): pick a style from the Creative Library of the
 * workspace you are acting in — one made in FiasWorks Studio, say — and the
 * Image Generator draws in it. The server adds the style's pictures and words
 * to the request; nothing here reads them.
 */
import { useQuery } from '@tanstack/react-query';
import { Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { CreativeStudioLink } from '@/components/CreativeStudioLink';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { CreativeStyleChoice } from '../types/image-generator.types';

interface CreativeStylePickerProps {
  value: CreativeStyleChoice | null | undefined;
  onChange: (choice: CreativeStyleChoice | null) => void;
  disabled?: boolean;
}

export function CreativeStylePicker({ value, onChange, disabled }: CreativeStylePickerProps) {
  const { selectedHolderId } = useHolderSwitcher();
  const holderId = selectedHolderId ?? null;
  const styles = useQuery({
    queryKey: queryKeys.vault.creative(holderId ?? '', 'style'),
    queryFn: async () => (await api.listCreativeItems(holderId!, 'style')).items,
    enabled: !!holderId,
    staleTime: 60_000,
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={value ? 'secondary' : 'ghost'}
          size="icon"
          className="h-7 w-7 flex-shrink-0"
          disabled={disabled || !holderId}
          title={value ? `Style: ${value.name}` : 'Draw in one of your styles'}
          aria-label="Choose a style"
        >
          <Palette className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="end">
        <p className="px-1 pb-2 text-xs font-medium text-muted-foreground">Your styles</p>
        {styles.isLoading ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">Loading…</p>
        ) : styles.isError ? (
          <p className="px-1 py-2 text-xs text-destructive">Your styles could not be loaded.</p>
        ) : (styles.data ?? []).length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            No styles yet.{' '}
            <CreativeStudioLink>{(studio) => `Make one in ${studio}`}</CreativeStudioLink>
          </p>
        ) : (
          <ul
            className="max-h-64 overflow-y-auto space-y-0.5"
            role="listbox"
            aria-label="Your styles"
          >
            {(styles.data ?? []).map((style) => (
              <li key={style.itemId}>
                <button
                  type="button"
                  role="option"
                  aria-selected={value?.styleId === style.itemId}
                  className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted aria-selected:bg-muted"
                  onClick={() =>
                    onChange({ styleId: style.itemId, name: style.name, holderId: holderId! })
                  }
                >
                  <span className="block truncate">{style.name}</span>
                  {style.words && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {style.words}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {value && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full h-7 text-xs"
            onClick={() => onChange(null)}
          >
            No style
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
